// THE "ABOUT THE PLACE" TEXT INPUT — a multiline field with a LIVE CHARACTER COUNT against Apple's
// 5–500 bound.
//
// ⚠️ WHY A CUSTOM FIELD RATHER THAN `multiline: true`. Apple caps this text at 500 characters
// (`AppleDataSpecificationReference.rtf:148`). A plain FireCMS string field shows no count, so the
// first time anyone learns a description is 40 characters too long is when the backend refuses the
// whole booking-button step — after they have written it, saved it, and queued it. There is no test
// runner in this package and nothing else here can catch an over-long value, so the counter IS the
// feedback loop.
//
// ⚠️ IT DOES NOT TRUNCATE, AND MUST NOT. `maxLength` on the input would silently swallow a staff
// member's last sentence mid-typing. The count turns red and the backend refuses with a named
// reason; that is the loud direction, and losing a client's words is the quiet one.
//
// ⚠️ NEWLINES ARE ALLOWED HERE AND ARE NOT STRIPPED. This is one of only two properties Apple
// allow-lists `\n` on (`AppleDataSpecificationReference.rtf:305-310`) — everywhere else in this
// integration a line break is forbidden. The field says so, because the rule is the opposite of the
// one staff learn from every other text box on this document, and because a well-meaning "clean up
// the whitespace" pass by a human would flatten a client's paragraphs. `\r` and `\t` ARE forbidden;
// those come from pasting out of Word, so the field warns rather than rewriting the text.
//
// ⚠️ THE WRITE BOUNDARY IS UNTOUCHED. This calls `setValue`, which puts the string into the FORM. The
// document write still goes through the collection's single `saveEntity` path and its `onPreSave`
// strip, exactly like every other field. No `firebase/firestore` import here.
//
// ⚠️ THE BACKEND GATE STAYS. This is a convenience that moves a refusal earlier. Every number it
// shows is transcribed from `apple-location-content.schema.ts`, which re-validates server-side and
// refuses with NO Apple call.

import { FieldHelperText, FieldProps, LabelWithIconAndTooltip } from "@firecms/core";
import { NotesIcon, TextField, Typography } from "@firecms/ui";

import { ABOUT_TEXT_MAX_LENGTH, ABOUT_TEXT_MIN_LENGTH } from "./apple_location_content.types";

/** What the counter should say, and how alarmed it should look. */
const countState = (text: string): { count: number; tone: string; message: string | null } => {
  // Trimmed, because the backend measures the trimmed value at both bounds — a count that ignored
  // that would tell staff they are inside a limit they are actually outside.
  const count = text.trim().length;

  if (count === 0) {
    return { count, tone: "text-text-secondary dark:text-text-secondary-dark", message: null };
  }
  if (count < ABOUT_TEXT_MIN_LENGTH) {
    return {
      count,
      tone: "text-red-600 dark:text-red-400",
      message: `Apple needs at least ${ABOUT_TEXT_MIN_LENGTH} characters.`,
    };
  }
  if (count > ABOUT_TEXT_MAX_LENGTH) {
    return {
      count,
      tone: "text-red-600 dark:text-red-400",
      message: `${count - ABOUT_TEXT_MAX_LENGTH} over Apple's limit. This will be refused until it is shortened.`,
    };
  }
  return { count, tone: "text-text-secondary dark:text-text-secondary-dark", message: null };
};

/** `\r` and `\t` are invisible in a textarea, so they are named rather than silently removed. */
const forbiddenWhitespaceWarning = (text: string): string | null => {
  const found = [
    text.includes("\t") ? "a tab" : null,
    text.includes("\r") ? "a carriage return" : null,
  ].filter(Boolean);
  if (found.length === 0) return null;
  return (
    `This text contains ${found.join(" and ")}, which Apple forbids. It usually comes from pasting ` +
    `out of a document — retype the affected line. Line breaks (Enter) are fine.`
  );
};

/**
 * Reads the SIBLING `locale` off the live form values.
 *
 * ⚠️ WHY THIS FIELD CARES ABOUT A FIELD IT DOES NOT OWN. Apple requires a locale whenever there IS a
 * description (`AppleLocationGuidance.rtf:978`), and the backend refuses the whole booking-button
 * step if one is missing. The locale input sits BELOW this box and is easy to skip, and the failure
 * only surfaces after the step has been queued and refused. This box is where a staff member is
 * already being told about length, so it is where the reminder belongs.
 *
 * Read defensively: `context.values` is the whole entity, and every level of the path is optional on
 * a part-filled form.
 */
const siblingLocale = (values: unknown, propertyKey: string): string => {
  // `propertyKey` is the full dotted path to THIS field, e.g. `locationCta.input.about.text`.
  const path = propertyKey.split(".").slice(0, -1).concat("locale");
  let cursor: unknown = values;
  for (const segment of path) {
    if (typeof cursor !== "object" || cursor === null) return "";
    cursor = (cursor as Record<string, unknown>)[segment];
  }
  return typeof cursor === "string" ? cursor : "";
};

export const AppleAboutTextField = (props: FieldProps<string>) => {
  const {
    value,
    setValue,
    propertyKey,
    property,
    includeDescription,
    showError,
    error,
    disabled,
    context,
  } = props;

  const text = value ?? "";
  const { count, tone, message } = countState(text);
  const whitespaceWarning = forbiddenWhitespaceWarning(text);
  // Only once there is something to label. An empty box needs no locale, and warning about one would
  // be noise on every untouched record.
  const localeMissing =
    text.trim().length > 0 && siblingLocale(context?.values, propertyKey).trim().length === 0;

  return (
    <>
      <LabelWithIconAndTooltip
        icon={<NotesIcon size="small" />}
        propertyKey={propertyKey}
        title={property.name || propertyKey}
      />

      <TextField
        value={text}
        multiline
        minRows={4}
        maxRows={14}
        disabled={disabled}
        error={
          showError || count > ABOUT_TEXT_MAX_LENGTH || (count > 0 && count < ABOUT_TEXT_MIN_LENGTH)
        }
        // An emptied box clears the field rather than saving `""`, which would fail the backend's
        // 5-character floor. ⚠️ This alone is NOT what makes an empty description safe: clearing
        // `text` leaves the surrounding `about` map behind, so the backend also normalises a
        // text-less `about` away (`OptionalAboutLocationInputSchema`). Both halves are needed.
        onChange={(event) => setValue(event.target.value === "" ? null : event.target.value)}
      />

      <div className="mt-1 flex flex-wrap items-baseline justify-between gap-2">
        <Typography variant="caption" className="text-text-secondary dark:text-text-secondary-dark">
          Line breaks are allowed in this field — they are not allowed anywhere else on this
          document. Leave it empty to send no description at all.
        </Typography>
        <Typography variant="caption" className={tone}>
          {count} / {ABOUT_TEXT_MAX_LENGTH}
        </Typography>
      </div>

      {message && (
        <Typography variant="caption" className="text-red-600 dark:text-red-400">
          {message}
        </Typography>
      )}
      {whitespaceWarning && (
        <Typography variant="caption" className="text-red-600 dark:text-red-400">
          {whitespaceWarning}
        </Typography>
      )}
      {localeMissing && (
        <Typography variant="caption" className="text-red-600 dark:text-red-400">
          Set the “Language” field below — Apple requires one whenever there is a description, and
          the whole booking-button step is refused without it. Enter “en” for English.
        </Typography>
      )}

      <FieldHelperText
        includeDescription={includeDescription}
        showError={showError}
        error={error}
        property={property}
      />
    </>
  );
};
